import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useLanguage } from '../../contexts/lang/languageContext';
import { setTenantReportTypeList } from '../../slices/reportDataSlice';
import { RootState } from '../../utils/store';
import { useToastNotificationContext } from '../global/alert/useToastNotification';
import { useAppSelector } from '../global/typedReduxHooks';

import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { useTenantSlug } from '~/features/tenant/helpers';
import { LocalizationService } from '~/services/api/localization/endpoints';
import { TenantService } from '~/services/api/tenant/endpoints';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

export const useLocalizedReportTypeData = () => {
  const slug = useTenantSlug();
  const { selectedLanguage } = useLanguage();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const reportTypeQuery = LocalizationService.endpoints.reportType.useQuery(
    {
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
    },
    { skip: !slug || !selectedChannel },
  );

  const getLocalizedReportType = useCallback(
    (reportType: string, translate = '') => {
      if (reportTypeQuery.isSuccess) {
        if (translate !== 'original') {
          const reportTypeFound = reportTypeQuery.data.find(
            (type) => type.name === reportType,
          );
          if (reportTypeFound) {
            const translation =
              reportTypeFound.translations[selectedLanguage.code];
            return translation ? translation.name : reportType;
          }
          return reportType;
        } else {
          const reportTypeFound = reportTypeQuery.data.find(
            (type) =>
              type.translations[selectedLanguage.code]?.name === reportType,
          );
          if (reportTypeFound) {
            return reportTypeFound.name;
          } else {
            return reportType;
          }
        }
      }
      return reportType;
    },
    [reportTypeQuery.data, reportTypeQuery.isSuccess, selectedLanguage.code],
  );

  return { getLocalizedReportType };
};

const useReportTypeData = () => {
  const reportTypeList = useSelector(
    (state: RootState) => state.reportData.allReportTypes,
  );
  const dispatch = useDispatch();
  const { selectedChannel } = useAppSelector(
    (state) => state.intakeChannelData,
  );
  const { selectedLanguage } = useLanguage();
  const [prevLanguage, setPrevLanguage] = useState(selectedLanguage);
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const isFetchingRef = useRef(false);

  const slug = useTenantSlug();
  const { selectedTenant } = usePartnerContext();

  const query = TenantService.endpoints.intakeChannelProgramDetails.useQuery(
    {
      slug: selectedTenant.slug || slug,
      intakeChannelId: selectedChannel ?? -1,
      params: {
        language_id: selectedLanguage.id,
      },
    },
    { skip: !selectedChannel || !slug },
  );

  if (query.isSuccess) {
    dispatch(setTenantReportTypeList(query.data.report_types));
  }

  const fetchReportTypeList = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    await query.refetch();
    if (query.isSuccess) {
      dispatch(setTenantReportTypeList(query.data.report_types));
      isFetchingRef.current = false;
    } else if (query.isError)
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Error Occurred While fetching report types',
        }),
        'error',
      );
  }, [createToastNotification, dispatch, intl, query]);

  useEffect(() => {
    const hasLanguageChanged =
      selectedLanguage && prevLanguage.id !== selectedLanguage.id;

    if (hasLanguageChanged) {
      fetchReportTypeList();
      setPrevLanguage(selectedLanguage);
    }
  }, [fetchReportTypeList, prevLanguage.id, selectedLanguage]);

  return { reportTypeList, fetchReportTypeList };
};

export default useReportTypeData;
